import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaPlus, FaTrash, FaCheckCircle, FaPaperclip, FaEdit, FaSave, FaTimes } from "react-icons/fa";
import api from "../../../libs/axios";
import { useAuth } from "../../../provider/auth.provider";
import { useConfirm } from "../../../provider/confirm.provider";
import { SelectWithSearch, SelectOption } from "../../../utils/select-with-search";
import IncFilesPanel from "../components/IncFilesPanel";
import UnitSelect from "../components/UnitSelect";

// `locked` comes from inc_sites.is_locked — set by "Finalize Billing". A locked
// site is frozen and the API answers 423 on every write, so the write controls
// are folded into the permission flags rather than left on screen to fail.
const MeasurementBookTab: React.FC<{ siteId: string; locked?: boolean }> = ({ siteId, locked = false }) => {
  const { hasPermission } = useAuth();
  const { confirm } = useConfirm();
  // Opening the tab and creating a book. Viewing survives the lock; every
  // write below does not.
  const canManage = hasPermission("IncSite", "measurement_book");
  const canCreate = canManage && !locked;
  // Adding, editing and removing the measured lines.
  const canEdit = hasPermission("IncSite", "mb_edit") && !locked;
  // Uploading and deleting the book's documents.
  const canManageFiles = hasPermission("IncSite", "mb_files") && !locked;
  // Signing the book off, which is what advances the workflow.
  const canComplete = hasPermission("IncSite", "mb_approve") && !locked;
  const [books, setBooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [remarks, setRemarks] = useState("");

  // add-item state keyed by mb id
  const [itemFor, setItemFor] = useState<
    Record<number, { item: SelectOption | null; qty: string; unit: string; billable: boolean; remarks: string }>
  >({});
  // Per-MB attachment counts, so the header can say how many documents exist
  // without expanding the panel.
  const [fileCounts, setFileCounts] = useState<Record<number, number>>({});

  // Inline edit of one measured line, keyed by item id. Only one row is ever
  // open at a time, which keeps "save" unambiguous.
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [editItemForm, setEditItemForm] = useState({ qty: "", unit: "", billable: true, remarks: "" });
  const [savingItem, setSavingItem] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/inc/sites/${siteId}/measurement-books`);
      const rows: any[] = res.data?.data || [];
      setBooks(rows);

      // Counts only — the file list itself is fetched by IncFilesPanel. A single
      // failed count must not take the whole tab down.
      const counts = await Promise.all(
        rows.map(async (mb) => {
          try {
            const files = await api.get(`/inc/measurement-books/${mb.id}/files`);
            return [mb.id, (files.data?.data || []).length] as const;
          } catch {
            return [mb.id, 0] as const;
          }
        }),
      );
      setFileCounts(Object.fromEntries(counts));
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    load();
  }, [load]);

  const createBook = async () => {
    await api.post(`/inc/sites/${siteId}/measurement-books`, { remarks });
    setRemarks("");
    load();
  };
  const approve = async (id: number) => {
    await api.post(`/inc/measurement-books/${id}/approve`, {});
    load();
  };
  const startEditItem = (it: any) => {
    setEditingItem(it.id);
    setEditItemForm({
      qty: String(it.quantity ?? ""),
      unit: it.unit || "",
      billable: it.billable !== false,
      remarks: it.remarks || "",
    });
  };

  const saveEditItem = async (itemId: number) => {
    if (editItemForm.qty !== "" && !(Number(editItemForm.qty) >= 0)) {
      return toast.error("Quantity must be 0 or more");
    }
    setSavingItem(true);
    try {
      await api.put(`/inc/mb-items/${itemId}`, {
        quantity: Number(editItemForm.qty) || 0,
        unit: editItemForm.unit,
        billable: editItemForm.billable,
        remarks: editItemForm.remarks,
      });
      setEditingItem(null);
      load();
    } catch {
      /* the axios interceptor already surfaced the message */
    } finally {
      setSavingItem(false);
    }
  };

  const removeItem = async (itemId: number) => {
    const ok = await confirm({
      title: "Remove this line?",
      message: "The measured line is deleted from the measurement book.",
      confirmText: "Remove",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/inc/mb-items/${itemId}`);
      load();
    } catch {
      /* the axios interceptor already surfaced the message */
    }
  };

  const addItem = async (mbId: number) => {
    const st = itemFor[mbId];
    if (!st?.item) return toast.error("Select an item");
    await api.post(`/inc/measurement-books/${mbId}/items`, {
      material_item_id: st.item.id,
      quantity: Number(st.qty) || 0,
      unit: st.unit,
      billable: st.billable,
      remarks: st.remarks,
    });
    setItemFor((p) => ({ ...p, [mbId]: { item: null, qty: "", unit: "", billable: true, remarks: "" } }));
    load();
  };

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;

  return (
    <div>
      {locked && (
        <p className="text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded px-3 py-2 mb-3">
          Billing has been finalized for this site — it is locked and this tab is read-only.
        </p>
      )}
      {canCreate && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">New measurement book remarks</label>
            <input value={remarks} onChange={(e) => setRemarks(e.target.value)} className="w-full px-2 py-2 text-sm border border-gray-300 rounded" />
          </div>
          <button onClick={createBook} className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-800 rounded-md hover:bg-blue-900">
            <FaPlus className="w-3 h-3" /> New MB
          </button>
        </div>
      )}

      {books.length === 0 ? (
        <p className="text-sm text-gray-500">No measurement books.</p>
      ) : (
        <div className="space-y-4">
          {books.map((mb) => {
            const st = itemFor[mb.id] || { item: null, qty: "", unit: "", billable: true, remarks: "" };
            const fileCount = fileCounts[mb.id] ?? 0;
            return (
              <div key={mb.id} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-gray-800">
                    MB #{mb.id}
                    {mb.client_signed ? (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">Completed</span>
                    ) : (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">Pending</span>
                    )}
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-full">
                      <FaPaperclip className="w-2.5 h-2.5" /> {fileCount} file{fileCount === 1 ? "" : "s"}
                    </span>
                  </div>
                  {canComplete && !mb.client_signed && (
                    <button onClick={() => approve(mb.id)} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-green-700 border border-green-200 bg-green-50 rounded hover:bg-green-100">
                      <FaCheckCircle className="w-3 h-3" /> Complete
                    </button>
                  )}
                </div>

                {/* Book-level remarks, captured when the MB was created. */}
                {mb.remarks && (
                  <p className="text-xs text-gray-600 mb-2">
                    <span className="font-semibold text-gray-500">Remarks: </span>
                    {mb.remarks}
                  </p>
                )}

                {(mb.items || []).length > 0 && (
                  <table className="min-w-full text-sm mb-2">
                    <thead className="text-xs uppercase text-gray-500">
                      <tr>
                        <th className="text-left py-1">Item</th>
                        <th className="text-right py-1">Qty</th>
                        <th className="text-left py-1 pl-3">Unit</th>
                        <th className="text-center py-1">Billable</th>
                        <th className="text-left py-1 pl-3">Remarks</th>
                        {canEdit && <th className="text-right py-1">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {mb.items.map((it: any) =>
                        editingItem === it.id ? (
                          <tr key={it.id} className="border-t border-gray-100 bg-blue-50/40">
                            <td className="py-1 pr-2">
                              {it.material_item ? `${it.material_item.item_code} — ${it.material_item.item_name}` : `#${it.material_item_id}`}
                            </td>
                            <td className="py-1">
                              <input
                                type="number"
                                min={0}
                                value={editItemForm.qty}
                                onChange={(e) => setEditItemForm({ ...editItemForm, qty: e.target.value })}
                                className="w-20 px-2 py-1 text-sm border border-gray-300 rounded text-right"
                              />
                            </td>
                            <td className="py-1 pl-3">
                              <UnitSelect
                                value={editItemForm.unit}
                                onChange={(unit) => setEditItemForm({ ...editItemForm, unit })}
                                className="w-28"
                              />
                            </td>
                            <td className="py-1 text-center">
                              <input
                                type="checkbox"
                                checked={editItemForm.billable}
                                onChange={(e) => setEditItemForm({ ...editItemForm, billable: e.target.checked })}
                              />
                            </td>
                            <td className="py-1 pl-3">
                              <input
                                value={editItemForm.remarks}
                                onChange={(e) => setEditItemForm({ ...editItemForm, remarks: e.target.value })}
                                className="w-full min-w-[120px] px-2 py-1 text-sm border border-gray-300 rounded"
                              />
                            </td>
                            <td className="py-1">
                              <div className="flex justify-end gap-1">
                                <button
                                  onClick={() => saveEditItem(it.id)}
                                  disabled={savingItem}
                                  title="Save"
                                  className="p-1.5 text-green-700 hover:bg-green-50 rounded disabled:opacity-60"
                                >
                                  <FaSave className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => setEditingItem(null)}
                                  title="Cancel"
                                  className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                                >
                                  <FaTimes className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr key={it.id} className="border-t border-gray-100">
                            <td className="py-1">{it.material_item ? `${it.material_item.item_code} — ${it.material_item.item_name}` : `#${it.material_item_id}`}</td>
                            <td className="py-1 text-right">{it.quantity}</td>
                            <td className="py-1 pl-3">{it.unit || "-"}</td>
                            <td className="py-1 text-center">{it.billable ? "Yes" : "No"}</td>
                            <td className="py-1 pl-3 text-gray-600 break-words max-w-xs">{it.remarks || "-"}</td>
                            {canEdit && (
                              <td className="py-1">
                                <div className="flex justify-end gap-1">
                                  <button onClick={() => startEditItem(it)} title="Edit" className="p-1.5 text-blue-700 hover:bg-blue-50 rounded">
                                    <FaEdit className="w-3 h-3" />
                                  </button>
                                  <button onClick={() => removeItem(it.id)} title="Remove" className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                                    <FaTrash className="w-3 h-3" />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                )}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <IncFilesPanel
                    filesUrl={`/inc/measurement-books/${mb.id}/files`}
                    deleteUrl={(fileId) => `/inc/mb-files/${fileId}`}
                    folderName="inc/measurement-books"
                    canManage={canManageFiles}
                    title="Measurement book documents"
                  />
                </div>

                {canEdit && !mb.client_signed && (
                  <div className="flex flex-wrap gap-2 items-end mt-3">
                    <div className="min-w-[200px] flex-1"><SelectWithSearch value={st.item} onChange={(o) => setItemFor((p) => ({ ...p, [mb.id]: { ...st, item: o } }))} apiEndpoint="/inc/material-items" dataKey="items" labelKey="item_name" displayFormat={(i) => `${i.item_code} — ${i.item_name}`} placeholder="Item..." minSearchLength={0} /></div>
                    <input placeholder="Qty" value={st.qty} onChange={(e) => setItemFor((p) => ({ ...p, [mb.id]: { ...st, qty: e.target.value } }))} className="w-24 px-2 py-2 text-sm border border-gray-300 rounded" />
                    <UnitSelect value={st.unit} onChange={(unit) => setItemFor((p) => ({ ...p, [mb.id]: { ...st, unit } }))} className="w-32" />
                    <input placeholder="Remarks" value={st.remarks} onChange={(e) => setItemFor((p) => ({ ...p, [mb.id]: { ...st, remarks: e.target.value } }))} className="min-w-[160px] flex-1 px-2 py-2 text-sm border border-gray-300 rounded" />
                    <label className="flex items-center gap-1 text-sm pb-2"><input type="checkbox" checked={st.billable} onChange={(e) => setItemFor((p) => ({ ...p, [mb.id]: { ...st, billable: e.target.checked } }))} /> Billable</label>
                    <button onClick={() => addItem(mb.id)} className="px-3 py-2 text-sm text-white bg-blue-800 rounded hover:bg-blue-900">Add Item</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MeasurementBookTab;
